import _ from "lodash";
import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { Modal, Button } from "@toddle-edu/ds-web";
import { useI18n } from "Hooks";
import {
  ParticipantsModalProvider,
  TAB_TYPES,
  useParticipantsModal,
} from "./context/ParticipantsModalContext";
import useParticipantsData from "./hooks/useParticipantsData";
import ParticipantTabs from "./components/ParticipantTabs";
import ParticipantSearch from "./components/ParticipantSearch";
import ParticipantList from "./components/ParticipantList";
import classes from "./ParticipantsModal.scss";

const MODAL_WIDTH = "480px";
const MODAL_HEIGHT = "560px";

const modalBodyStyle = {
  paddingBottom: "0px",
};

/**
 * Inner content component — uses ParticipantsModalContext and data hook.
 */
const ParticipantsModalContent = ({ onClose, label }) => {
  const { t } = useI18n(["calendar", "common"]);
  const {
    eventTitle,
    staffCount,
    studentCount,
    familyCount,
  } = useParticipantsModal();
  const {
    isLoading,
    isFetchingMore,
    data,
    hasNextPage,
    loadMore,
    hasGrades,
    hasCourses,
    gradeIds,
    gradeDetailsMap,
    gradeSearchResults,
    isSearchLoading,
    isGradeCountsLoading,
    onGradeRangeChanged,
    courseIds,
    courseDetailsMap,
    courseSearchResults,
    isCourseCountsLoading,
    onCourseRangeChanged,
    customDetailsMap,
    filteredActiveTabIds,
    onCustomRangeChanged,
  } = useParticipantsData();

  const totalCount = staffCount + studentCount + familyCount;
  const listData = data?.listData ?? {};
  const gradesCount = _.size(_.get(listData, TAB_TYPES.GRADES, []));
  const coursesCount = _.size(_.get(listData, TAB_TYPES.COURSES, []));

  return (
    <>
      <Modal.Header
        title={
          label
            ? `${label} (${totalCount})`
            : t("calendar:participants_count", { count: totalCount })
        }
        subtitle={eventTitle}
        isClosable={true}
        testId="participants-modal-header"
        dsVersion="2.0"
      />

      <Modal.Body
        testId="participants-modal-body"
        isScrollable={true}
        dsVersion="2.0"
        style={modalBodyStyle}
      >
        <div className={classes.modalBody}>
          <ParticipantTabs
            hasGrades={hasGrades}
            hasCourses={hasCourses}
            gradesCount={gradesCount}
            coursesCount={coursesCount}
            testId={"participants-modal-tabs"}
          />
          <ParticipantSearch />
          <ParticipantList
            isLoading={isLoading}
            listData={listData}
            hasNextPage={hasNextPage}
            isFetchingMore={isFetchingMore}
            loadMore={loadMore}
            gradeIds={gradeIds}
            gradeDetailsMap={gradeDetailsMap}
            gradeSearchResults={gradeSearchResults}
            isSearchLoading={isSearchLoading}
            isGradeCountsLoading={isGradeCountsLoading}
            onGradeRangeChanged={onGradeRangeChanged}
            courseIds={courseIds}
            courseDetailsMap={courseDetailsMap}
            courseSearchResults={courseSearchResults}
            isCourseCountsLoading={isCourseCountsLoading}
            onCourseRangeChanged={onCourseRangeChanged}
            customDetailsMap={customDetailsMap}
            filteredActiveTabIds={filteredActiveTabIds}
            onCustomRangeChanged={onCustomRangeChanged}
          />
        </div>
      </Modal.Body>

      <Modal.Footer testId="participants-modal-footer" dsVersion="2.0">
        <div className={classes.footerActions}>
          <Button
            variant="neutral"
            type="outlined"
            dsVersion="2.0"
            onClick={onClose}
            testId="participants-modal-close-btn"
          >
            {t("common:close")}
          </Button>
        </div>
      </Modal.Footer>
    </>
  );
};

ParticipantsModalContent.propTypes = {
  onClose: PropTypes.func.isRequired,
  label: PropTypes.string,
};

/**
 * ParticipantsModal — standalone modal for viewing event participants.
 * Provides ParticipantsModalContext to all child components.
 */
const ParticipantsModal = ({
  isOpen,
  onClose,
  eventTitle,
  associatedEntities,
  sharedWith,
  staffCount,
  studentCount,
  familyCount,
  label = "",
  eventType,
}) => {
  const initialTab = useMemo(() => {
    const hasGradesInEntities = _.some(
      associatedEntities,
      item => _.get(item, "__typename") === "Grade"
    );
    const hasCoursesInEntities = _.some(
      associatedEntities,
      item => _.get(item, "__typename") === "Course"
    );
    if (hasGradesInEntities) return TAB_TYPES.GRADES;
    if (hasCoursesInEntities) return TAB_TYPES.COURSES;
    if (_.includes(sharedWith, "STUDENT")) return TAB_TYPES.STUDENTS;
    if (_.includes(sharedWith, "PARENT")) return TAB_TYPES.FAMILY;
    if (_.includes(sharedWith, "STAFF")) return TAB_TYPES.STAFF;
    return TAB_TYPES.STUDENTS;
  }, [associatedEntities, sharedWith]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      width={MODAL_WIDTH}
      height={MODAL_HEIGHT}
      testId="participants-modal"
      dsVersion="2.0"
    >
      <ParticipantsModalProvider
        initialTab={initialTab}
        eventTitle={eventTitle}
        associatedEntities={associatedEntities}
        sharedWith={sharedWith}
        staffCount={staffCount}
        studentCount={studentCount}
        familyCount={familyCount}
        eventType={eventType}
      >
        <ParticipantsModalContent onClose={onClose} label={label} />
      </ParticipantsModalProvider>
    </Modal>
  );
};

ParticipantsModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  eventTitle: PropTypes.string,
  associatedEntities: PropTypes.array,
  sharedWith: PropTypes.array,
  staffCount: PropTypes.number,
  studentCount: PropTypes.number,
  familyCount: PropTypes.number,
  eventType: PropTypes.string,
};

ParticipantsModal.defaultProps = {
  eventTitle: "",
  associatedEntities: [],
  sharedWith: [],
  staffCount: 0,
  studentCount: 0,
  familyCount: 0,
  eventType: undefined,
};

export default ParticipantsModal;
